/**
 * CommandPaletteItem
 *
 * Renders a single command in the palette with icon, label, context hint, and shortcut badge.
 */

import type { CommandDefinition } from '../types/commandTypes';
import { CommandItem } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { useShortcutLabel } from '@/features/keybindings/hooks/useShortcutLabel';
import type { KeybindingCommandId } from '@/features/keybindings/model/keybindingTypes';

type CommandPaletteItemProps = {
  command: CommandDefinition;
  onSelect: () => void;
  /** Current explorer context hint (e.g. "MyServer > AdventureWorks") */
  contextHint?: string | null;
};

/**
 * Metadata (shortcut / rightHint) lives in a fixed-width right column so its
 * left edge never jitters with label length (PP-33) — before this, it just
 * flowed after the label via `ml-auto`, so its rendered width (and therefore
 * its start position) tracked the metadata's own content length. 200px sits
 * in the middle of the agreed 180-220px band.
 */
const METADATA_COLUMN_WIDTH = 'w-[200px]';

/**
 * 70% contrast (of the required 70-75% band) translated literally: render
 * the metadata in the SAME color the label uses — `--popover-foreground`,
 * inherited from `Command`'s `text-popover-foreground` — at 70% opacity via
 * Tailwind's alpha modifier, against the `--popover` surface (aliased to
 * `--surface-raised`) the palette floats on. This ties the metadata's
 * intensity directly to the label's own color at a stated fraction, rather
 * than swapping to `--muted-foreground` — a separately-calibrated token whose
 * own contrast ratio against these surfaces sits far below 70-75% of the
 * label's (see the CH-09 audit note at the top of src/index.css) — which
 * would read closer to "disabled" than "de-emphasized". 70 (not 72-75) is
 * used because it is already this codebase's dominant opacity-modifier value.
 */
const METADATA_TEXT_CLASS = 'text-popover-foreground/70';

/**
 * G-10 — the context breadcrumb's own reserved column. T5.7 stabilised the
 * metadata column above and disclosed that `contextHint` had the identical
 * symptom: it flowed inline after the label, so its start position tracked the
 * label's text length and every row began its breadcrumb at a different x.
 *
 * Fixed with the same mechanism the wave has now used four times, and in the
 * form PP-26 established for exactly this shape (a variable-length label
 * followed by a value that must share a left edge): a fixed label track in a
 * `grid-cols-[Npx_1fr]` template. Reserving the LABEL — rather than the hint —
 * is what pins the hint's start x, and it keeps the breadcrumb reading next to
 * the command it qualifies instead of banished to the row's right edge.
 *
 * 240px clears the longest `needsContext` label in the registry ("Migrate
 * Database to Connection…", 33 chars) and still leaves the breadcrumb ~224px
 * of the 512px (`max-w-lg`) palette — comparable to the 200px the metadata
 * column opposite it gets. No command currently carries both a context hint
 * and a shortcut, and if one ever does the label track holds while the hint
 * and metadata truncate independently.
 */
const CONTEXT_LABEL_COLUMNS = 'grid-cols-[240px_1fr]';

function KeybindingHint({
  commandId,
  paletteCommandId,
}: {
  commandId: KeybindingCommandId;
  paletteCommandId: string;
}) {
  const shortcutLabel = useShortcutLabel(commandId);
  if (!shortcutLabel) return null;

  return (
    <span
      className={cn(
        'ml-3 flex shrink-0 justify-end tabular-nums',
        METADATA_COLUMN_WIDTH
      )}
      data-testid={`command-hint-${paletteCommandId}`}
    >
      <kbd
        className={cn(
          'inline-flex h-5 min-w-0 max-w-full shrink items-center gap-0.5 truncate rounded border border-border bg-muted px-1.5 text-[10px] font-medium',
          METADATA_TEXT_CLASS
        )}
      >
        {shortcutLabel}
      </kbd>
    </span>
  );
}

export function CommandPaletteItem({ command, onSelect, contextHint }: CommandPaletteItemProps) {
  const Icon = command.icon;
  const isDisabled = command.isEnabled ? !command.isEnabled() : false;
  const showContext = command.needsContext && contextHint;
  const rightHint = command.rightHint?.();

  return (
    <CommandItem
      value={command.id}
      onSelect={onSelect}
      disabled={isDisabled}
      keywords={[command.label, ...(command.keywords ?? [])]}
      data-testid={`command-item-${command.id}`}
    >
      {Icon && <Icon className="mr-2 h-4 w-4 shrink-0 opacity-70" />}
      {/* Label truncates independently of the metadata column — a long
          label never pushes the metadata out of view. */}
      {showContext ? (
        // Fixed label track (see CONTEXT_LABEL_COLUMNS): the breadcrumb starts
        // at the same x on every row regardless of how long the label is, and
        // the two truncate independently.
        <span className={cn('flex-1 min-w-0 grid items-baseline gap-2', CONTEXT_LABEL_COLUMNS)}>
          <span className="truncate" data-testid={`command-label-${command.id}`}>
            {command.label}
          </span>
          <span
            className="truncate text-xs text-muted-foreground/60"
            data-testid={`command-context-${command.id}`}
          >
            {contextHint}
          </span>
        </span>
      ) : (
        <span className="flex-1 min-w-0 truncate" data-testid={`command-label-${command.id}`}>
          {command.label}
        </span>
      )}
      {rightHint ? (
        // Fixed-width column, right-anchored in the row. `justify-end` hugs
        // short content (e.g. "Ctrl+K") to the row's right edge; long
        // content shrinks to the column width and truncates on its own —
        // independent of whatever the label above did. `tabular-nums` is
        // applied once here (inherited by both the rightHint span and the
        // shortcut `kbd` below) rather than duplicated on each — it only
        // touches digit glyphs, so it's a no-op on letter-only shortcuts
        // like "Ctrl+B" and only matters where a digit actually appears.
        <span
          className={cn(
            'ml-3 flex shrink-0 justify-end tabular-nums',
            METADATA_COLUMN_WIDTH
          )}
          data-testid={`command-hint-${command.id}`}
        >
          <span className={cn('min-w-0 truncate font-mono text-[10px]', METADATA_TEXT_CLASS)}>
            {rightHint}
          </span>
        </span>
      ) : command.keybindingId ? (
        <KeybindingHint commandId={command.keybindingId} paletteCommandId={command.id} />
      ) : null}
    </CommandItem>
  );
}
